import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Users,
  XCircle,
  Lock,
  DollarSign,
  Zap,
  Award,
  Wallet,
  RefreshCw
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/Tabs";
import { useWallet } from "../../context/WalletContext";
import { useContractServices } from "../../context/ContractServicesContext";
import { projectAPI, taskAPI } from "../../services/api";
import EmissionsTab from "../../components/portfolio-component/EmissionsTab";
import StakingPortfolioTab from "../../components/portfolio-component/StakingPortfolioTab";
import { ethers } from "ethers";
import skyLoader from "../../assets/home-assets/skyLoader.gif";
import UserTaskBoard from "../../components/portfolio-component/UserTaskBoardTab";
// Helper components for better organization
export const ProjectCard = ({
  project,
  showAmount = false,
  showStaking = false,
  showIU = false,
}) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="bg-gradient-to-r from-indigo-300 to-purple-300  rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          {project.status && (
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                project.status === "active"
                  ? "bg-green-100 text-green-800"
                  : project.status === "planning"
                  ? "bg-blue-100 text-blue-800"
                  : project.status === "completed"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </span>
          )}
          {project.primaryDomain && (
            <span className="px-2 py-0.5 text-xs bg-yellow-200 text-red-800 rounded-full">
              {project.primaryDomain}
            </span>
          )}
        </div>

        <h3 className="font-semibold text-black text-lg mb-2 line-clamp-1">
          {project.title}
        </h3>

        <p className="text-black font-medium text-sm mb-4 line-clamp-2">
          {project.shortDescription || project.description}
        </p>

        <div className="flex justify-between items-center font-medium text-xs text-gray-500 mb-3">
          <div className="flex items-center">
            <Calendar size={14} className="mr-1" />
            <span>{formatDate(project.createdAt)}</span>
          </div>

          <div className="flex items-center">
            <Users size={14} className="mr-1" />
            <span>{project.contributors?.length || 0} contributors</span>
          </div>
        </div>

        {showIU && project.iuHolding && (
          <div className="mt-3 p-2 bg-indigo-50 rounded-md">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-indigo-700">
                IU Holdings:
              </span>
              <span className="text-sm font-semibold text-indigo-800">
                {project.iuHolding} IUs ({project.ownershipPercentage || "0"}%)
              </span>
            </div>
          </div>
        )}

        {showAmount && project.investedAmount && (
          <div className="mt-3 p-2 bg-blue-50 rounded-md">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-blue-700">
                Invested Amount:
              </span>
              <span className="text-sm font-semibold text-blue-800">
                {project.investedAmount} CEL
              </span>
            </div>
          </div>
        )}

        {showStaking && project.stakedAmount && (
          <div className="mt-3 p-2 bg-purple-50 rounded-md">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-purple-700">
                Staked Amount:
              </span>
              <span className="text-sm font-semibold text-purple-800">
                {project.stakedAmount} CEL
              </span>
            </div>
            {project.totalStakedAmount && (
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs font-medium text-purple-700">
                  Total Staked:
                </span>
                <span className="text-sm font-semibold text-purple-800">
                  {project.totalStakedAmount} CEL
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 bg-indigo-200 p-3 bg-gray-50">
        <Link
          to={`/projects/token/${project.projectTokenId}`}
          className="w-full flex justify-center items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
        >
          View Project <ArrowRight size={16} className="ml-1" />
        </Link>
      </div>
    </div>
  );
};

const TaskCard = ({ task }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: {
        bg: "bg-green-100",
        text: "text-green-800",
        icon: <CheckCircle size={14} className="mr-1" />,
      },
      in_progress: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        icon: <Clock size={14} className="mr-1" />,
      },
      proposal_submitted: {
        bg: "bg-indigo-100",
        text: "text-indigo-800",
        icon: <FileText size={14} className="mr-1" />,
      },
      proposal_approved: {
        bg: "bg-purple-100",
        text: "text-purple-800",
        icon: <CheckCircle size={14} className="mr-1" />,
      },
      proposal_rejected: {
        bg: "bg-red-100",
        text: "text-red-800",
        icon: <XCircle size={14} className="mr-1" />,
      },
      work_submitted: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        icon: <FileText size={14} className="mr-1" />,
      },
    };

    const config = statusConfig[status] || statusConfig.in_progress;

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} flex items-center`}
      >
        {config.icon}
        {status
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No deadline";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 transition hover:shadow-md">
      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-semibold text-gray-900">{task.title}</h3>
          {getStatusBadge(task.status)}
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {task.description}
        </p>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Project:</span>
            <div className="font-medium text-gray-900">
              {task.projectTitle || "Unknown Project"}
            </div>
          </div>

          <div>
            <span className="text-gray-500">Deadline:</span>
            <div className="font-medium text-gray-900">
              {formatDate(task.dueDate)}
            </div>
          </div>

          <div>
            <span className="text-gray-500">Reward:</span>
            <div className="font-medium text-indigo-600">
              {task.taskPoints || task.reward || "—"} IUs
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 p-3 bg-gray-50">
        <Link
          to={`/tasks/${task._id}`}
          className="w-full flex justify-center items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
        >
          View Task <ArrowRight size={16} className="ml-1" />
        </Link>
      </div>
    </div>
  );
};

// StakedProjectCard component for the Projects I've Staked In section
export const StakedProjectCard = ({ projectId, projectName, score, stakedAmount }) => {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-300 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-in-out group flex flex-col h-full">
      {/* Header with Project ID and Name */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4">
        <h3 className="text-lg font-bold truncate">
          {projectName}
        </h3>
        <div className="text-xs text-purple-100 mt-1">
          Project ID: {projectId}
        </div>
      </div>
      
      {/* Score and Amount Section */}
      <div className="p-4 flex-grow">
        <div className="mb-3">
          <div className="text-sm text-gray-500 mb-1">Your Staking Score</div>
          <div className="flex items-center">
            <Award className="h-5 w-5 text-indigo-500 mr-2" />
            <span className="text-xl font-bold text-indigo-700">{typeof score === 'string' ? parseFloat(score).toFixed(2) : score}</span>
          </div>
        </div>
        
        {stakedAmount && (
          <div className="mb-3">
            <div className="text-sm text-gray-500 mb-1">Your Staked Amount</div>
            <div className="flex items-center">
              <Wallet className="h-5 w-5 text-purple-500 mr-2" />
              <span className="text-lg font-semibold text-purple-700">{stakedAmount} CEL</span>
            </div>
          </div>
        )}
        
        {/* Info about staking score */}
        <div className="bg-indigo-50 p-3 rounded-lg text-xs text-indigo-700 mt-2">
          <p>Staking score affects your influence and emission rewards for this project</p>
        </div>
      </div>
      
      {/* Footer */}
      <div className="border-t border-gray-200 p-4 bg-purple-50 hover:bg-indigo-50 transition">
        <Link
          to={`/projects/token/${projectId}`}
          className="flex justify-center items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition"
        >
          View Project <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

function Portfolio() {
  const { walletAddress } = useWallet();
  const { services, isReady } = useContractServices();
  const [activeTab, setActiveTab] = useState("myProjects");
  const [projects, setProjects] = useState({
    created: [],
    contributed: [],
    invested: [],
    staked: [],
  });
  const [stakedProjects, setStakedProjects] = useState([]);
  const [loadingStakedProjects, setLoadingStakedProjects] = useState(false);
  const [iuHoldings, setIuHoldings] = useState([]);
  const [loadingIuHoldings, setLoadingIuHoldings] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [activeTaskFilter, setActiveTaskFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshingStaked, setRefreshingStaked] = useState(false);
  const [refreshingIuHoldings, setRefreshingIuHoldings] = useState(false);

  // Fetch all necessary data when component mounts
  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if wallet is connected
        if (!walletAddress) {
          setError("Please connect your wallet to view your portfolio");
          setLoading(false);
          return;
        }

        // Fetch tasks - in a separate try-catch to prevent failure
        let tasksData = [];
        try {
          const tasksResponse = await taskAPI.getByWalletAddress(walletAddress);
          tasksData = tasksResponse.data || [];
          // Clear error flag if tasks API succeeds
          localStorage.removeItem("tasks_api_error");
        } catch (tasksErr) {
          console.warn(
            "Error fetching tasks - This API might not be implemented yet:",
            tasksErr
          );
          // Set flag in localStorage to indicate tasks API error
          localStorage.setItem("tasks_api_error", "true");
          // Continue with an empty array for tasks
        }

        setTasks(tasksData);
        setFilteredTasks(tasksData);

        // Fetch blockchain data for projects if contract services are ready
        if (isReady && services) {
          try {
            // Fetch IU holdings (Created, Contributed, Invested projects)
            await fetchIuHoldings();
            
            // Fetch staked projects
            await fetchStakedProjects();
          } catch (blockchainErr) {
            console.error("Error fetching blockchain data:", blockchainErr);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching portfolio data:", err);
        setError(err.message || "Failed to load portfolio data");
        setLoading(false);
      }
    };

    fetchPortfolioData();
  }, [walletAddress, services, isReady]);

  // Function to fetch IU holdings (created, contributed, invested projects)
  const fetchIuHoldings = async () => {
    if (!isReady || !services || !services.innovationUnits || !walletAddress) {
      return;
    }
    
    try {
      setLoadingIuHoldings(true);
      
      // Get all IU holdings with role identification
      const holdings = await services.innovationUnits.getAllUserIUHoldings(walletAddress);
      
      // Log the holdings with roles for debugging
      console.log(`Found ${holdings.length} projects with IU holdings`, holdings);
      
      // Set the state with the role-identified holdings
      setIuHoldings(holdings);
      
      // Update projects state with filtered holdings
      const createdProjects = holdings.filter(h => h.role === "creator");
      const contributedProjects = holdings.filter(h => h.role === "contributor");
      const investedProjects = holdings.filter(h => h.role === "investor");
      
      setProjects(prev => ({
        ...prev,
        created: createdProjects,
        contributed: contributedProjects,
        invested: investedProjects
      }));
      
    } catch (error) {
      console.error("Error fetching IU holdings:", error);
    } finally {
      setLoadingIuHoldings(false);
    }
  };

  // Function to fetch staked projects
  const fetchStakedProjects = async () => {
    if (!isReady || !services || !services.projectStaking || !walletAddress) {
      return;
    }
    
    try {
      setLoadingStakedProjects(true);
      
      // Get projects where user has staked
      const projectIds = await services.projectStaking.getUserStakedProjects(walletAddress);
      console.log(`Found ${projectIds.length} staked projects for ${walletAddress}`);
      
      if (projectIds && projectIds.length > 0) {
        // Get details for each staked project
        const projectsWithScores = await Promise.all(
          projectIds.map(async (projectId) => {
            try {
              // Get project staking score for user
              const score = await services.projectStaking.getUserProjectScore(projectId, walletAddress);
              
              // Get user staked amount
              const stakedAmount = await services.projectStaking.getUserTotalStakedAmount(projectId, walletAddress);
              const formattedStakedAmount = ethers.utils.formatEther(stakedAmount);
              
              // Get project name
              let projectName = `Project #${projectId}`;
              try {
                projectName = await services.innovationUnits.getProjectName(projectId);
              } catch (nameError) {
                console.log(`Could not get project name for project ${projectId}:`, nameError);
              }
              
              return {
                projectId,
                projectName,
                score: parseFloat(score.formatted).toFixed(2),
                rawScore: score.raw,
                stakedAmount: formattedStakedAmount
              };
            } catch (error) {
              console.error(`Error getting details for staked project ${projectId}:`, error);
              return {
                projectId,
                projectName: `Project #${projectId}`,
                score: '0',
                rawScore: 0,
                stakedAmount: '0'
              };
            }
          })
        );
        
        setStakedProjects(projectsWithScores);
      } else {
        setStakedProjects([]);
      }
    } catch (error) {
      console.error('Error fetching staked projects from blockchain:', error);
      setStakedProjects([]);
    } finally {
      setLoadingStakedProjects(false);
    }
  };

  // Function to refresh blockchain data
  const refreshBlockchainData = async () => {
    setRefreshingIuHoldings(true);
    await fetchIuHoldings();
    setRefreshingIuHoldings(false);
    
    setRefreshingStaked(true);
    await fetchStakedProjects();
    setRefreshingStaked(false);
  };

  const filterTasks = (status) => {
    setActiveTaskFilter(status);

    if (status === "all") {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(tasks.filter((task) => task.status === status));
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen ">
          <img 
            src={skyLoader} 
            alt="Loading..." 
            className="w-100 h-100 object-contain"
          />
        </div>
    );

  if (error)
    return (
      <div className="min-h-screen p-8 flex justify-center items-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center mb-4">
            <AlertCircle className="text-red-500 mr-2" size={24} />
            <h2 className="text-xl font-semibold text-red-700">Error</h2>
          </div>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen p-8 bg-gradient-to-b from-indigo-50 via-white to-indigo-100">
        <h1 className="text-2xl font-bold mb-4 ">My Portfolio</h1>
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-8 flex flex-wrap">
              <TabsTrigger value="myProjects">My Projects</TabsTrigger>
              <TabsTrigger value="tasks">Tasks</TabsTrigger>
              <TabsTrigger value="staking">Staking</TabsTrigger>
              <TabsTrigger value="emissions">Emissions</TabsTrigger>
            </TabsList>

            <TabsContent value="myProjects">
              <div className="space-y-6">
                <div className="space-y-6 mb-10">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold flex items-center">
                      <Users className="mr-2" size={20} />
                      My Created Projects
                    </h2>
                    <Link
                      to="/projects/create"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Create New Project
                    </Link>
                  </div>

                  {loadingIuHoldings ? (
                    <div className="flex justify-center items-center p-8">
                      <div className="animate-spin h-8 w-8 border-b-2 border-indigo-600 rounded-full"></div>
                      <span className="ml-3 text-indigo-600 font-medium">Loading created projects...</span>
                    </div>
                  ) : projects.created.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                      <div className="flex justify-center mb-4">
                        <AlertCircle className="text-gray-400" size={48} />
                      </div>
                      <h3 className="text-lg font-medium text-gray-700 mb-2">
                        No projects found
                      </h3>
                      <p className="text-gray-500 max-w-md mx-auto">
                        You haven't created any projects yet. Get started by creating your first project.
                      </p>
                      <Link
                        to="/projects/create"
                        className="mt-4 inline-block px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        Create Your First Project
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {projects.created.map((holding) => (
                        <div key={holding.projectId} className="bg-gradient-to-r from-indigo-300 to-purple-300  rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                          <div className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-2 py-0.5 text-xs bg-green-200 text-green-800 rounded-full">
                                Creator
                              </span>
                            </div>

                            <h3 className="font-semibold text-black text-lg mb-2 line-clamp-1">
                              {holding.projectName}
                            </h3>

                            <p className="text-black font-medium text-sm mb-4">
                              Project ID: {holding.projectId}
                            </p>

                            <div className="mt-3 p-2 bg-indigo-50 rounded-md">
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-indigo-700">
                                  IU Holdings:
                                </span>
                                <span className="text-sm font-semibold text-indigo-800">
                                  {holding.formattedBalance} IUs ({holding.percentage}%)
                                </span>
                              </div>
                            </div>
                            
                            
                          </div>

                          <div className="border-t border-gray-100 bg-indigo-200 p-3">
                            <Link
                              to={`/projects/token/${holding.projectId}`}
                              className="w-full flex justify-center items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              View Project <ArrowRight size={16} className="ml-1" />
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-6 mb-10">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold flex items-center">
                      <FileText className="mr-2" size={20} />
                      Projects I Contribute To
                    </h2>
                    <Link
                      to="/projects"
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Find More Projects
                    </Link>
                  </div>

                  {loadingIuHoldings ? (
                    <div className="flex justify-center items-center p-8">
                      <div className="animate-spin h-8 w-8 border-b-2 border-indigo-600 rounded-full"></div>
                      <span className="ml-3 text-indigo-600 font-medium">Loading contributed projects...</span>
                    </div>
                  ) : projects.contributed.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                      <p className="text-gray-500">
                        You're not contributing to any projects yet. Explore projects to find contribution opportunities.
                      </p>
                      <Link
                        to="/projects"
                        className="mt-4 inline-block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Explore Projects
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {projects.contributed.map((holding) => (
                        <div key={holding.projectId} className="bg-gradient-to-r from-indigo-300 to-purple-300  rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                          <div className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-2 py-0.5 text-xs bg-blue-200 text-blue-800 rounded-full">
                                Contributor
                              </span>
                            </div>

                            <h3 className="font-semibold text-black text-lg mb-2 line-clamp-1">
                              {holding.projectName}
                            </h3>

                            <p className="text-black font-medium text-sm mb-4">
                              Project ID: {holding.projectId}
                            </p>

                            <div className="mt-3 p-2 bg-indigo-50 rounded-md">
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-indigo-700">
                                  IU Holdings:
                                </span>
                                <span className="text-sm font-semibold text-indigo-800">
                                  {holding.formattedBalance} IUs ({holding.percentage}%)
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="border-t border-gray-100 bg-indigo-200 p-3">
                            <Link
                              to={`/projects/token/${holding.projectId}`}
                              className="w-full flex justify-center items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              View Project <ArrowRight size={16} className="ml-1" />
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-6 mb-10">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold flex items-center">
                      <DollarSign className="mr-2" size={20} />
                      Projects I've Invested In
                    </h2>
                    <Link
                      to="/projects"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Invest in Projects
                    </Link>
                  </div>

                  {loadingIuHoldings ? (
                    <div className="flex justify-center items-center p-8">
                      <div className="animate-spin h-8 w-8 border-b-2 border-indigo-600 rounded-full"></div>
                      <span className="ml-3 text-indigo-600 font-medium">Loading invested projects...</span>
                    </div>
                  ) : projects.invested.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                      <p className="text-gray-500">
                        You haven't invested in any projects yet. Explore projects to find investment opportunities.
                      </p>
                      <Link
                        to="/projects"
                        className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Find Investment Opportunities
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {projects.invested.map((holding) => (
                        <div key={holding.projectId} className="bg-gradient-to-r from-indigo-300 to-purple-300  rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                          <div className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-2 py-0.5 text-xs bg-purple-200 text-purple-800 rounded-full">
                                Investor
                              </span>
                            </div>

                            <h3 className="font-semibold text-black text-lg mb-2 line-clamp-1">
                              {holding.projectName}
                            </h3>

                            <p className="text-black font-medium text-sm mb-4">
                              Project ID: {holding.projectId}
                            </p>

                            <div className="mt-3 p-2 bg-indigo-50 rounded-md">
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-indigo-700">
                                  IU Holdings:
                                </span>
                                <span className="text-sm font-semibold text-indigo-800">
                                  {holding.formattedBalance} IUs ({holding.percentage}%)
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="border-t border-gray-100 bg-indigo-200 p-3">
                            <Link
                              to={`/projects/token/${holding.projectId}`}
                              className="w-full flex justify-center items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              View Project <ArrowRight size={16} className="ml-1" />
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold flex items-center">
                      <Zap className="mr-2" size={20} />
                      Projects I've Staked In
                    </h2>
                    <div className="flex gap-2">
                      <button
                        onClick={refreshBlockchainData}
                        disabled={refreshingStaked || refreshingIuHoldings || loadingStakedProjects || loadingIuHoldings}
                        className="px-3 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors flex items-center disabled:opacity-50"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {refreshingStaked || refreshingIuHoldings ? 'Refreshing...' : 'Refresh Data'}
                      </button>
                      <Link
                        to="/projects"
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                      >
                        Stake in Projects
                      </Link>
                    </div>
                  </div>

                  {loadingStakedProjects ? (
                    <div className="flex justify-center items-center p-8">
                      <div className="animate-spin h-8 w-8 border-b-2 border-indigo-600 rounded-full"></div>
                      <span className="ml-3 text-indigo-600 font-medium">Loading staked projects...</span>
                    </div>
                  ) : stakedProjects.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                      <p className="text-gray-500">
                        You haven't staked in any projects yet. Explore projects to find staking opportunities.
                      </p>
                      <Link
                        to="/projects"
                        className="mt-4 inline-block px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                      >
                        Find Staking Opportunities
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {stakedProjects.map((project) => (
                        <StakedProjectCard 
                          key={project.projectId}
                          projectId={project.projectId}
                          projectName={project.projectName}
                          score={project.score}
                          stakedAmount={project.stakedAmount}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Add User Task Board Tab Content */}

            <TabsContent value="tasks">
              <UserTaskBoard />
            </TabsContent>
                
            {/* Add Staking Tab Content */}
            <TabsContent value="staking">
              <StakingPortfolioTab />
            </TabsContent>

            {/* Add Emissions Tab Content */}
            <TabsContent value="emissions">
              <EmissionsTab />
            </TabsContent>
          </Tabs>
        </div>
    </div>
  );
}

export default Portfolio;
